"use client";

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Elements, PaymentElement, useStripe, useElements, LinkAuthenticationElement } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, CreditCard, Shield, Clock } from "lucide-react";
import { useStripePayment } from "@/hooks/useStripePayment";
import { ServiceType } from "./booking";
import { supabase } from "@/integrations/supabase/client";

// Initialize Stripe - fail loudly if publishable key is missing
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
if (!STRIPE_PUBLISHABLE_KEY) {
  console.error('[CRITICAL] VITE_STRIPE_PUBLISHABLE_KEY is missing from environment variables');
} else {
  // Production-safe logging: show key prefix and last 4 chars only
  const keyPrefix = STRIPE_PUBLISHABLE_KEY.substring(0, 8); // "pk_test_" or "pk_live_"
  const keyLast4 = STRIPE_PUBLISHABLE_KEY.slice(-4);
  console.log(`[Stripe] Initializing with ${keyPrefix}...${keyLast4}`);
}
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

interface StripePaymentFormProps {
  businessId?: string;  // Optional - component handles loading state internally
  selectedService?: ServiceType;  // Optional - component handles loading state internally
  tipAmount: number;
  customerInfo?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  onPaymentSuccess?: (paymentData?: {
    stripeCustomerId?: string;
    paymentMethodId?: string;
    paymentIntentId?: string;
  }) => void;
  onPaymentError?: (error: string) => void;
  hasDateTimeSelected?: boolean;
  bookingId?: string;  // Booking ID to link PaymentIntent to booking for webhook processing
  // Terms-of-Service acceptance gates the Pay button (not the form's visibility),
  // so customers can enter card details first and accept terms last.
  termsAccepted?: boolean;
}


// Wrapper component that provides Stripe Elements context
export function StripePaymentForm(props: StripePaymentFormProps) {
  const { createPaymentIntent } = useStripePayment();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [isUpdatingTip, setIsUpdatingTip] = useState(false);
  const [paymentData, setPaymentData] = useState<{
    stripeCustomerId?: string;
    paymentIntentId?: string;
  } | null>(null);

  // Track initial tip amount to detect changes
  const initialTipRef = useRef(props.tipAmount);
  const updateTimeoutRef = useRef<NodeJS.Timeout>();
  
  // Track whether we've already created a PaymentIntent to prevent recreation on validation toggles
  const intentCreatedRef = useRef(false);

  // Check if required props are available (loading state handled internally)
  const hasRequiredProps = props.businessId && props.selectedService && props.onPaymentSuccess && props.onPaymentError;

  // Check if customer info is complete
  const hasCustomerInfo = props.customerInfo &&
    props.customerInfo.firstName &&
    props.customerInfo.lastName &&
    props.customerInfo.email;


  // Create payment intent ONCE when customerInfo becomes available - do NOT recreate on validation toggles
  useEffect(() => {
    // Wait for required props and customer info before creating PaymentIntent
    if (!hasRequiredProps || !hasCustomerInfo || !props.customerInfo) return;
    
    // Prevent re-creation if we've already created one for this session
    // This handles the case where hasCustomerInfo toggles true -> false -> true
    if (intentCreatedRef.current) return;

    const initializePayment = async () => {
      setIsCreatingIntent(true);
      intentCreatedRef.current = true; // Mark as created before async call
      try {
        const result = await createPaymentIntent({
          businessId: props.businessId!,
          serviceId: props.selectedService!.id,
          amount: props.selectedService!.price,
          tipAmount: props.tipAmount,
          customerInfo: {
            customerName: `${props.customerInfo!.firstName} ${props.customerInfo!.lastName}`,
            customerEmail: props.customerInfo!.email,
            customerPhone: props.customerInfo!.phone,
          },
          bookingId: props.bookingId,
        });

        setClientSecret(result.clientSecret);
        setPaymentData({
          stripeCustomerId: result.stripeCustomerId,
          paymentIntentId: result.paymentIntentId,
        });
        initialTipRef.current = props.tipAmount;
        console.log("[StripePaymentForm] Payment intent initialized successfully");
      } catch (error: any) {
        console.error("[StripePaymentForm] Error creating payment intent:", error);
        // Reset flag so user can retry if they change customer info
        intentCreatedRef.current = false;
        const message = error.message || "Failed to initialize payment. Please try again.";
        props.onPaymentError?.(message);
      } finally {
        setIsCreatingIntent(false);
      }
    };

    initializePayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRequiredProps, hasCustomerInfo]); // Only run when required props and customer info are available

  // PaymentIntent amount is updated on tip change to ensure charged amount always matches UI total
  const updatePaymentIntentAmount = useCallback(async (newTipAmount: number) => {
    if (!paymentData?.paymentIntentId || !props.businessId || !clientSecret) {
      return;
    }

    setIsUpdatingTip(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-connect', {
        body: {
          action: 'updatePaymentIntent',
          paymentIntentId: paymentData.paymentIntentId,
          clientSecret,
          businessId: props.businessId,
          tipAmount: newTipAmount,
        },
      });

      if (error) {
        console.error('Error updating payment intent:', error);
        toast.error('Failed to update tip amount');
      }
    } catch (error) {
      console.error('Error updating payment intent:', error);
    } finally {
      setIsUpdatingTip(false);
    }
  }, [paymentData?.paymentIntentId, props.businessId, clientSecret]);

  // Debounced tip update - wait 500ms after user stops changing tip
  useEffect(() => {
    // Only update if PaymentIntent has been created and tip has changed
    if (paymentData?.paymentIntentId && props.tipAmount !== initialTipRef.current) {
      // Clear any pending update
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      // Schedule update after 500ms of inactivity
      updateTimeoutRef.current = setTimeout(() => {
        updatePaymentIntentAmount(props.tipAmount);
        initialTipRef.current = props.tipAmount;
      }, 500);
    }

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [props.tipAmount, paymentData?.paymentIntentId, updatePaymentIntentAmount]);

  // Memoize Elements options to prevent remounting on props changes
  // MUST be called before any early returns to comply with Rules of Hooks
  const elementsOptions = useMemo(
    () => ({
      clientSecret,
      appearance: {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: 'hsl(var(--primary))',
          colorBackground: 'hsl(var(--background))',
          colorText: 'hsl(var(--foreground))',
          colorDanger: 'hsl(var(--destructive))',
          fontFamily: 'Inter, system-ui, sans-serif',
          spacingUnit: '4px',
          borderRadius: '8px',
        },
      },
      loader: 'auto' as const,
    }),
    [clientSecret]
  );

  // CRITICAL: Fail loudly if Stripe publishable key is missing
  if (!stripePromise) {
    return (
      <Card className="border-destructive">
        <CardContent className="py-8">
          <div className="text-center space-y-4">
            <div className="text-destructive text-lg font-semibold">Payment System Configuration Error</div>
            <p className="text-sm text-muted-foreground">
              Stripe publishable key is missing. Please contact support.
            </p>
            <p className="text-xs text-muted-foreground font-mono">
              Error: VITE_STRIPE_PUBLISHABLE_KEY not configured
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show loading state if required props (businessId, selectedService, callbacks) are not ready
  if (!hasRequiredProps) {
    return (
      <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center space-y-2">
              <CreditCard className="h-8 w-8 mx-auto text-muted-foreground animate-pulse" />
              <p className="text-sm text-muted-foreground">
                Loading payment form...
              </p>
            </div>
          </CardContent>
        </Card>
    );
  }

  // Show placeholder if customer info is not ready
  if (!hasCustomerInfo) {
    return (
      <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center space-y-2">
              <CreditCard className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Please complete your personal details above to continue with payment
              </p>
            </div>
          </CardContent>
        </Card>
    );
  }

  if (isCreatingIntent) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center space-y-2">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground">Preparing secure payment...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!clientSecret) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-destructive">Unable to initialize payment. Please try again.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <StripePaymentElementForm {...props} paymentData={paymentData} />
    </Elements>
  );
}

// The actual payment form component
function StripePaymentElementForm({
  onPaymentSuccess,
  onPaymentError,
  customerInfo,
  selectedService,
  tipAmount,
  paymentData,
  hasDateTimeSelected = true,
  termsAccepted = true,
}: StripePaymentFormProps & {
  paymentData: {
    stripeCustomerId?: string;
    paymentIntentId?: string;
  } | null;
}) {
  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours}h`;
    return `${hours}h ${remainingMinutes}m`;
  };
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [isElementReady, setIsElementReady] = useState(false);
  const [email, setEmail] = useState(customerInfo.email);
  const [paymentElementComplete, setPaymentElementComplete] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }

    const correlationId = paymentData?.paymentIntentId || 'UNKNOWN';
    console.log(`[PAYMENT-FLOW:${correlationId}] [1/7] Pay button clicked`);

    setIsLoading(true);
    setInlineError(null);

    try {
      console.log(`[PAYMENT-FLOW:${correlationId}] [2/7] Starting confirmPayment...`);

      // Submit the form data to Stripe
      const { error: submitError } = await elements.submit();

      if (submitError) {
        console.error(`[PAYMENT-FLOW:${correlationId}] [3/7] elements.submit() failed:`, submitError);
        const errorMsg = submitError.message || "Payment submission failed";
        setInlineError(errorMsg);
        onPaymentError?.(errorMsg);
        setIsLoading(false);
        return;
      }

      // Confirm the payment.
      //
      // `redirect: 'if_required'` keeps non-redirect cards inline (the
      // happy-path branch below calls `onPaymentSuccess` directly). For
      // redirect-required methods (3DS challenges, wallets, BNPL) Stripe
      // redirects the browser to `return_url` and we resume the flow there.
      //
      // The full 3DS-redirect contract (must keep these three pieces in sync):
      //   1. The parent flow (e.g. `magic-flow/BookingFlow.tsx`) writes a
      //      `figgy_pending_booking` payload to `sessionStorage` *before*
      //      this submit fires, so the post-redirect page can rehydrate.
      //   2. `return_url` points at `/payment-success`, registered in
      //      `src/App.tsx`.
      //   3. `src/pages/booking/PaymentSuccessPage.tsx` reads
      //      `payment_intent` + `redirect_status` from the URL, replays
      //      booking creation against the persisted session, and clears it.
      //
      // If you change the route, the storage key, or remove the
      // session-write in the parent flow, 3DS-required payments will land
      // on a stale-session screen.
      const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
          receipt_email: email,
        },
        redirect: 'if_required',
      });

      if (paymentError) {
        console.error(`[PAYMENT-FLOW:${correlationId}] [3/7] confirmPayment failed:`, {
          code: paymentError.code,
          message: paymentError.message,
          type: paymentError.type
        });
        const errorMsg = paymentError.message || "Payment failed";
        setInlineError(errorMsg);
        onPaymentError?.(errorMsg);
      } else {
        console.log(`[PAYMENT-FLOW:${correlationId}] [3/7] confirmPayment succeeded:`, {
          status: paymentIntent?.status,
          amount: paymentIntent?.amount,
          paymentMethodId: paymentIntent?.payment_method,
          paymentIntentId: paymentIntent?.id
        });

        // Payment succeeded - extract IDs from confirmed paymentIntent (authoritative source)
        const paymentMethodId = paymentIntent?.payment_method as string;
        const confirmedPaymentIntentId = paymentIntent?.id as string;

        console.log(`[PAYMENT-FLOW:${correlationId}] [4/7] Dispatching payment success event with confirmed payment_intent_id=${confirmedPaymentIntentId}...`);
        toast.success("Payment successful!");
        onPaymentSuccess?.({
          stripeCustomerId: paymentData?.stripeCustomerId,
          paymentMethodId: paymentMethodId,
          paymentIntentId: confirmedPaymentIntentId, // Use confirmed ID from Stripe response, not stale paymentData
        });
      }
    } catch (error: any) {
      console.error(`[PAYMENT-FLOW:${correlationId}] [3/7] Unexpected error during payment:`, error);
      const errorMsg = error.message || "An unexpected error occurred";
      setInlineError(errorMsg);
      onPaymentError?.(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const totalAmount = selectedService.price + tipAmount;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Complete Payment
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Link Authentication Element for Stripe Link */}
          <div>
            <LinkAuthenticationElement
              id="link-authentication-element"
              options={{
                defaultValues: {
                  email: customerInfo.email,
                },
              }}
              onChange={(event) => {
                if (event.value?.email) {
                  setEmail(event.value.email);
                }
              }}
            />
          </div>

          {/* Payment Element */}
          <div>
            <PaymentElement
              id="payment-element"
              options={{
                layout: 'tabs',
                wallets: {
                  applePay: 'auto',
                  googlePay: 'auto',
                },
                defaultValues: {
                  billingDetails: {
                    name: `${customerInfo.firstName} ${customerInfo.lastName}`,
                    email: customerInfo.email,
                    phone: customerInfo.phone,
                  },
                },
                fields: {
                  billingDetails: {
                    email: 'auto',
                    name: 'auto',
                    phone: 'auto',
                  },
                },
                terms: {
                  card: 'always',
                },
              }}
              onReady={() => setIsElementReady(true)}
              onChange={(event) => {
                setPaymentElementComplete(event.complete);
              }}
            />
          </div>

          {/* Payment Summary */}
          <div className="bg-muted/50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <div>
                <span>{selectedService.name}</span>
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  <Clock className="h-3 w-3" />
                  <span>{formatDuration(selectedService.duration)}</span>
                </div>
              </div>
              <span>${selectedService.price.toFixed(2)}</span>
            </div>
            {tipAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span>Tip</span>
                <span>${tipAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Total</span>
              <span>${totalAmount.toFixed(2)}</span>
            </div>
          </div>

          {/* Inline Error Display */}
          {inlineError && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
              <p className="text-sm text-destructive font-medium">{inlineError}</p>
            </div>
          )}

          {/* Date/Time Warning */}
          {!hasDateTimeSelected && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
              <p className="text-sm text-amber-800 font-medium">Please select a date and time before continuing</p>
            </div>
          )}

          {/* Terms Warning */}
          {!termsAccepted && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
              <p className="text-sm text-amber-800 font-medium">Please accept the Terms of Service below before paying</p>
            </div>
          )}

          {/* Submit Button - Only enabled when Stripe Elements validation passes */}
          <Button
            type="button"
            onClick={handleSubmit}
            className="w-full"
            size="lg"
            disabled={
              !isElementReady ||
              isLoading ||
              !stripe ||
              !elements ||
              !paymentElementComplete ||
              !hasDateTimeSelected ||
              !termsAccepted
            }
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Pay ${totalAmount.toFixed(2)}
              </>
            )}
          </Button>

          {/* Security Notice */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Shield className="h-3 w-3" />
            <span>Your payment is secure and encrypted</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}